/**
 * The geofence, running in the browser (brief §10).
 *
 * The shape of this file is the privacy story: it holds the position, it holds the hot-cell
 * list, and it makes the decision. What it sends is one request per res-5 region — ≈ 250 km²,
 * "somewhere around Stuttgart" — and nothing else ever leaves.
 *
 * The judgment itself is `domain/geofence.ts`, which is pure. Everything here is the parts
 * that cannot be: the clock, `watchPosition`, `localStorage`, and the Notification API.
 *
 * **What the PWA cannot do**, and the settings page says so in plain German: there is no
 * usable background geofencing API on the web. This runs while the app is open. The APK from
 * slice 8 gets a foreground service and works with the app closed.
 */

import type { Alert, AlertSettings, GeofenceState, HotCell } from '$domain/geofence.ts';
import { decide, describeAlert, EMPTY_STATE } from '$domain/geofence.ts';
import { cellFor, regionOf } from '$domain/h3.ts';
import type { DwellState } from '$domain/passby.ts';
import { updateDwell } from '$domain/passby.ts';
import { OfflineError, request } from './api.ts';
import { reverseGeocode } from './place.ts';
import { showLocalNotification } from './platform.ts';

const STATE_KEY = 'hotspotter.geofence.state';
/** How long a region's hot-cell list is reused before refetching. The score moves every ten. */
const FEED_TTL_MS = 5 * 60 * 1000;

/** What `/api/cells/hot` sends: the geofence fields plus the two the swipe deck needs. */
export type FeedCell = HotCell & {
  vibe: number | null;
  vibeVotes: number;
  topAttribute: string | null;
  facets: Record<string, number>;
  allMine: boolean;
  judgedAt: string | null;
};

type Feed = { region: string; cells: FeedCell[]; fetchedAt: number };

/**
 * One res-5 region's cells from `/api/cells/hot`, dates turned into numbers. The geofence asks
 * for the region it is in, and the camera view (amendment 18) for that one and the six round it;
 * neither asks for anything finer.
 */
export async function hotCellsIn(region: string): Promise<FeedCell[]> {
  const feed = await request<{ cells: (FeedCell & { lastSightingAt: string | null })[] }>(
    `/api/cells/hot?cell5=${region}`,
  );
  return feed.cells.map((entry) => ({
    ...entry,
    lastSightingAt:
      entry.lastSightingAt === null ? null : Date.parse(String(entry.lastSightingAt)),
  }));
}

function loadState(): GeofenceState {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (raw === null) return EMPTY_STATE;
    return { ...EMPTY_STATE, ...(JSON.parse(raw) as Partial<GeofenceState>) };
  } catch {
    // A corrupt entry means "alert as though this device were new", which is the safe way to
    // be wrong: at worst one extra notification, never a missed one.
    return EMPTY_STATE;
  }
}

function saveState(state: GeofenceState): void {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    // Private browsing, or a full quota. The geofence still works for this session; it just
    // forgets its cooldowns on the next one.
  }
}

/** `YYYY-MM-DD` in the device's own time zone, which is what "per calendar day" means. */
function localDay(now: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * One position watch, two consumers.
 *
 * The geofence needs it, and so does the swipe deck (brief §9a) — and a second
 * `watchPosition` would double the battery cost of the most expensive thing an app can ask a
 * phone for, to learn the same thing twice. So this class owns the watch and the hot-cell
 * feed, and publishes what it knows; `deck.svelte.ts` reads from it.
 */
class Alerts {
  /** The most recent alert, for the in-app banner. Cleared when the user dismisses it. */
  current = $state<Alert | undefined>();
  running = $state(false);
  /** Set when the browser refuses the position, so the UI can say why nothing happens. */
  problem = $state('');
  /** Whether alerts can actually be delivered while the app is in the background. */
  readonly backgroundCapable = false;

  /** Published for the swipe deck. */
  hotCells = $state<FeedCell[]>([]);
  position = $state<{ lat: number; lon: number } | undefined>();
  dwell = $state<DwellState>({ cell: null, since: 0 });

  #settings: AlertSettings | undefined;
  #state: GeofenceState = loadState();
  #feed: Feed | undefined;
  #watch: number | undefined;

  start(settings: AlertSettings): void {
    this.#settings = settings;
    if (this.#watch !== undefined) return;
    if (typeof navigator === 'undefined' || navigator.geolocation === undefined) return;

    this.#watch = navigator.geolocation.watchPosition(
      (fix) => {
        this.problem = '';
        void this.#onPosition(fix.coords.latitude, fix.coords.longitude);
      },
      (cause) => {
        this.problem =
          cause.code === cause.PERMISSION_DENIED
            ? 'Ohne Standortfreigabe kann nicht gewarnt werden.'
            : 'Position lässt sich gerade nicht bestimmen.';
      },
      // `enableHighAccuracy: false` on purpose: the question is which of seven hexagons you
      // are in, and a high-accuracy watch is the single most expensive thing an app can ask a
      // phone for. `maximumAge` lets the platform answer from its own recent fix.
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 30_000 },
    );
    this.running = true;
  }

  stop(): void {
    if (this.#watch !== undefined) navigator.geolocation.clearWatch(this.#watch);
    this.#watch = undefined;
    this.running = false;
  }

  update(settings: AlertSettings): void {
    this.#settings = settings;
    this.start(settings);
  }

  dismiss(): void {
    this.current = undefined;
  }

  async #onPosition(lat: number, lon: number): Promise<void> {
    const settings = this.#settings;
    if (settings === undefined) return;

    const cell = cellFor(lat, lon);
    const region = regionOf(cell);
    this.position = { lat, lon };
    // The dwell clock keeps running while you stay inside the ring, which is what makes "you
    // were there for a minute" mean something (brief §9a.1).
    this.dwell = updateDwell($state.snapshot(this.dwell), cell, Date.now());

    const cells = await this.#hotCells(region);
    if (cells === undefined) return;
    this.hotCells = cells;

    const now = new Date();
    const { alerts, state } = decide({
      position: { lat, lon },
      currentCell: cell,
      hotCells: cells,
      state: this.#state,
      settings,
      now: now.getTime(),
      localHour: now.getHours(),
      localDay: localDay(now),
    });

    this.#state = state;
    saveState(state);

    const alert = alerts[0];
    if (alert === undefined) return;
    this.current = alert;
    void this.#notify(alert, now.getTime());
  }

  async #hotCells(region: string): Promise<FeedCell[] | undefined> {
    const cached = this.#feed;
    if (cached?.region === region && Date.now() - cached.fetchedAt < FEED_TTL_MS) {
      return cached.cells;
    }
    try {
      const cells = await hotCellsIn(region);
      this.#feed = { region, cells, fetchedAt: Date.now() };
      return cells;
    } catch (cause) {
      // Offline is a state, never a dialogue (brief §14a.1). The last list we had is still
      // good enough to warn from — a hot cell does not stop being hot because the phone lost
      // signal, and slice 10 makes this cache explicit.
      if (cause instanceof OfflineError) return cached?.cells;
      return undefined;
    }
  }

  /**
   * A **local** notification. No server, no Web Push, nothing leaves the device (brief §10).
   */
  async #notify(alert: Alert, now: number): Promise<void> {
    const place = await reverseGeocode(alert.cell);
    const body = describeAlert(alert, place, now);
    const title = '⚠️ Erhöhte Geschicktendichte';

    // Inside the APK this goes through the Capacitor plugin, which works with the app closed
    // and involves no server at all (brief §15). In a browser it is a Notification, which
    // only works while the app is open — the settings page says so in plain German.
    if (await showLocalNotification(title, body)) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

    try {
      const registration = await navigator.serviceWorker?.getRegistration();
      const options = {
        body,
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        tag: `hotspot-${alert.cell}`,
      };
      // Through the service worker where there is one: on Android a page-created
      // Notification is dismissed the moment the tab is backgrounded, which is exactly when
      // somebody is walking around with the phone in their pocket.
      if (registration !== undefined) {
        await registration.showNotification('⚠️ Erhöhte Geschicktendichte', options);
      } else {
        new Notification('⚠️ Erhöhte Geschicktendichte', options);
      }
    } catch {
      // The banner in the app has already been set; a failed system notification is not
      // worth telling anybody about.
    }
  }
}

export const alerts = new Alerts();
